/**
 * Audit Service v2
 * POV-0050: Client-side service for v2 audit API
 * 
 * Provides methods to query audit history with filters, pagination,
 * correlation grouping, and integrity verification.
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import api from './api';
import { formatIST, formatTimeAgoIST, formatISTCompact } from '../utils/timezone';

const AUDIT_BASE_URL = '/api/v1/audit/v2';

/**
 * Get audit history for a part with filters
 * @param {string} partId - Part identifier (hpn_compact)
 * @param {object} options - Query options
 * @returns {Promise<object>} - Audit history response with items, pagination, filters_applied
 */
export const getPartAuditHistory = async (partId, options = {}) => {
  const {
    page = 1,
    pageSize = 25,
    sectionKey = null,
    action = null,
    actorId = null,
    correlationId = null,
    dateFrom = null,
    dateTo = null,
    searchQuery = null,
    controlledOnly = false,
  } = options;

  const params = new URLSearchParams();
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());
  
  if (sectionKey) params.set('section_key', sectionKey);
  if (action) params.set('action', action);
  if (actorId) params.set('actor_id', actorId);
  if (correlationId) params.set('correlation_id', correlationId);
  if (dateFrom) params.set('date_from', dateFrom.toISOString());
  if (dateTo) params.set('date_to', dateTo.toISOString());
  if (searchQuery) params.set('q', searchQuery);
  if (controlledOnly) params.set('controlled_only', 'true');

  const response = await api.get(`${AUDIT_BASE_URL}/events/part/${partId}?${params.toString()}`);
  return response.data;
};

/**
 * Get a single audit event by ID
 * @param {string} eventId - Event identifier
 * @returns {Promise<object>} - Audit event details
 */
export const getAuditEvent = async (eventId) => {
  const response = await api.get(`${AUDIT_BASE_URL}/events/${eventId}`);
  return response.data;
};

/**
 * Get all events in a correlation group
 * @param {string} correlationId - Correlation identifier
 * @returns {Promise<Array>} - Array of correlated events
 */
export const getCorrelationGroup = async (correlationId) => {
  const response = await api.get(`${AUDIT_BASE_URL}/events/correlation/${correlationId}`);
  return response.data;
};

/**
 * Get valid section keys with labels
 * @returns {Promise<Array>} - Array of {key, label} objects
 */
export const getSectionKeys = async () => {
  const response = await api.get(`${AUDIT_BASE_URL}/sections`);
  return response.data;
};

/**
 * Get audit statistics
 * @param {string|null} partId - Optional part filter
 * @returns {Promise<object>} - Statistics object
 */
export const getAuditStats = async (partId = null) => {
  const url = partId 
    ? `${AUDIT_BASE_URL}/stats?part_id=${partId}`
    : `${AUDIT_BASE_URL}/stats`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Verify audit log integrity (hash chain verification)
 * Requires admin or quality_manager role
 * @param {string|null} partId - Optional part filter
 * @returns {Promise<object>} - Verification result
 */
export const verifyAuditIntegrity = async (partId = null) => {
  const url = partId
    ? `${AUDIT_BASE_URL}/integrity/verify?part_id=${partId}`
    : `${AUDIT_BASE_URL}/integrity/verify`;
  const response = await api.post(url);
  return response.data;
};

/**
 * Export audit history to CSV
 * Requires admin or quality_manager role (POV-0056)
 * @param {string} partId - Part identifier
 * @param {object} filters - Filter options
 * @returns {Promise<Blob>} - CSV file blob
 */
export const exportAuditHistoryCSV = async (partId, filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.sectionKey) params.set('section_key', filters.sectionKey);
  if (filters.action) params.set('action', filters.action);
  if (filters.dateFrom) params.set('date_from', filters.dateFrom.toISOString());
  if (filters.dateTo) params.set('date_to', filters.dateTo.toISOString());
  if (filters.controlledOnly) params.set('controlled_only', 'true');
  
  const queryString = params.toString();
  const url = `${AUDIT_BASE_URL}/events/part/${partId}/export/csv${queryString ? `?${queryString}` : ''}`;
  
  const response = await api.get(url, {
    responseType: 'blob',
  });
  
  return response.data;
};

/**
 * Export audit history to PDF (HTML format for printing)
 * Requires admin or quality_manager role (POV-0056)
 * @param {string} partId - Part identifier
 * @param {object} filters - Filter options
 * @returns {Promise<Blob>} - HTML/PDF file blob
 */
export const exportAuditHistoryPDF = async (partId, filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.sectionKey) params.set('section_key', filters.sectionKey);
  if (filters.action) params.set('action', filters.action);
  if (filters.dateFrom) params.set('date_from', filters.dateFrom.toISOString());
  if (filters.dateTo) params.set('date_to', filters.dateTo.toISOString());
  if (filters.controlledOnly) params.set('controlled_only', 'true');
  
  const queryString = params.toString();
  const url = `${AUDIT_BASE_URL}/events/part/${partId}/export/pdf${queryString ? `?${queryString}` : ''}`;
  
  const response = await api.get(url, {
    responseType: 'blob',
  });
  
  return response.data;
};

/**
 * Download a blob as a file
 * @param {Blob} blob - File blob
 * @param {string} filename - File name
 */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Event action types for filtering
export const AUDIT_ACTIONS = [
  { value: 'CREATE', label: 'Created', icon: '➕' },
  { value: 'UPDATE', label: 'Updated', icon: '🔄' },
  { value: 'DELETE', label: 'Deleted', icon: '🗑️' },
  { value: 'RESTORE', label: 'Restored', icon: '↩️' },
  { value: 'CORRECTION', label: 'Corrected', icon: '✏️' },
  { value: 'LINK', label: 'Linked', icon: '🔗' },
  { value: 'UNLINK', label: 'Unlinked', icon: '⛓️' },
  { value: 'RELEASE', label: 'Released', icon: '✅' },
  { value: 'APPROVE', label: 'Approved', icon: '✓' },
  { value: 'REJECT', label: 'Rejected', icon: '✗' },
];

// Get action display info
export const getActionDisplay = (action) => {
  return AUDIT_ACTIONS.find(a => a.value === action) || { value: action, label: action, icon: '📝' };
};

// Format timestamp for display (IST)
export const formatTimestamp = (timestamp, options = {}) => {
  if (!timestamp) return '—';
  
  const { showRelative = true, showFull = false } = options;
  
  if (showFull) {
    return formatIST(timestamp);
  }
  
  if (showRelative) {
    const timeAgo = formatTimeAgoIST(timestamp);
    if (timeAgo) return timeAgo;
  }
  
  return formatISTCompact(timestamp);
};

export default {
  getPartAuditHistory,
  getAuditEvent,
  getCorrelationGroup,
  getSectionKeys,
  getAuditStats,
  verifyAuditIntegrity,
  exportAuditHistoryCSV,
  exportAuditHistoryPDF,
  downloadBlob,
  AUDIT_ACTIONS,
  getActionDisplay,
  formatTimestamp,
};
